import { getIdentityInstance } from '../schibstedAccount.js';
import { hasAccess } from '../paywall.js';
import { access } from '../svp/access.js';

import type { Vendor } from '../../types';
import type { ActorInput } from '@schibsted/pulse-sdk/dist/types/builders';
import type { ProviderAccessMap } from '@vgtv/svp-access';
import type { AppConfig } from '../../config.js';

const subscriptionNameMappings = {
  vgtv: {
    plus: 'VG+',
    sport: 'VG+ Sport',
  },
  ab: {
    plus: 'plus',
  },
} as const;

const getSubscriptionName = <T extends Exclude<Vendor, 'brandstudio'>>(
  provider: T,
  access: keyof (typeof subscriptionNameMappings)[T]
) => {
  return subscriptionNameMappings[provider][access];
};

const mapAccessesToSubscription = <T extends Vendor>(
  provider: T,
  accesses: ProviderAccessMap[keyof ProviderAccessMap][]
): string | undefined => {
  switch (provider) {
    case 'ab': {
      if (hasAccess('plus', accesses as ProviderAccessMap['ab'][])) {
        return getSubscriptionName(provider as 'ab', 'plus');
      }
      break;
    }
    case 'vgtv': {
      if (hasAccess('sport', accesses as ProviderAccessMap['vgtv'][])) {
        return getSubscriptionName(provider as 'vgtv', 'sport');
      }
      if (hasAccess('plus', accesses as ProviderAccessMap['vgtv'][])) {
        return getSubscriptionName(provider as 'vgtv', 'plus');
      }
      break;
    }
  }
  return 'No';
};

export const getActorObject = async (
  vendor: Vendor,
  actorRealm: AppConfig['pulse']['actorRealm']
): Promise<ActorInput> => {
  try {
    const [userId, accesses] = await Promise.all([
      getIdentityInstance().then((id) => id.getUserId().catch(() => null)),
      access.getAccesses(),
    ]);

    if (userId) {
      const subscriptionName = mapAccessesToSubscription(vendor, accesses);
      return {
        id: userId,
        realm: actorRealm,
        hasAnySubscription: !!subscriptionName && subscriptionName !== 'No',
        subscriptionName: subscriptionName,
      };
    }
  } catch (err) {
    console.error(err);
  }

  // pulse CIS sync expects actor to return an object with id,
  // even if the user is logged out
  return { id: undefined };
};
